import { inject, Injectable, LOCALE_ID } from '@angular/core';
import { Observable, of, Subject, timer } from 'rxjs';
import { map, share, take } from 'rxjs/operators';
import { TranslatableText, TranslatableTextOptions } from './translatable-text';
import { ICustomerAppsConfig } from '../config/config.module';
import { ConfigService } from '../config/config.service';
import { HttpClient } from '@angular/common/http';
import { DataResult, mapDataResult$ } from '../http/httpDataResponse';
import { localeMapping } from './locale-mapping';

interface TranslationRequest {
  text: TranslatableText;
  options: TranslatableTextOptions;
  subject: Subject<string>;
}

interface BatchGroup {
  requests: TranslationRequest[];
  options: TranslatableTextOptions;
}

interface TranslationApiResponse {
  id: string;
  sourceText: string;
  translatedText: string;
  sourceLang: string;
  targetLang: string;
  detectedSourceLang: string;
}

@Injectable({
  providedIn: 'root',
})
export class BatchedTranslationService {
  private httpClient = inject(HttpClient);
  private localeId: string = inject(LOCALE_ID);
  private configService = inject(ConfigService<ICustomerAppsConfig>);
  private pendingRequests: TranslationRequest[] = [];
  private batchTimer: any = null;
  private readonly BATCH_DELAY_MS = 300;

  // Cache for storing translations: key = cache key, value = translated text
  private translationCache = new Map<string, string>();

  private translationsLoadedSubject = new Subject<void>();
  translationsLoaded$ = this.translationsLoadedSubject.asObservable();

  getCachedTranslation(originalText: string, options: TranslatableTextOptions): string | null {
    const cacheKey = this.getCacheKey(originalText, options);
    return this.translationCache.get(cacheKey) ?? null;
  }

  /**
   * Request a translation for a single text item.
   * Returns an Observable that will emit the translated text when available.
   * Multiple requests within 300ms window with the same options are batched together.
   * Translations are cached to avoid redundant API calls when toggling between original/translated text.
   */
  translate(text: TranslatableText, options: TranslatableTextOptions): Observable<string> {
    const cacheKey = this.getCacheKey(text.original, options);

    if (this.translationCache.has(cacheKey)) {
      return of(this.translationCache.get(cacheKey)!);
    }

    const subject = new Subject<string>();

    const request: TranslationRequest = {
      text,
      options,
      subject,
    };

    this.pendingRequests.push(request);

    if (!this.batchTimer) {
      this.batchTimer = timer(this.BATCH_DELAY_MS)
        .pipe(take(1))
        .subscribe(() => {
          const pendingRequests = [...this.pendingRequests];
          this.pendingRequests = [];
          this.batchTimer = null;

          if (pendingRequests.length > 0) {
            this.groupRequestsByOptions(pendingRequests).forEach((group) => {
              this.processGroup(group);
            });
          }
        });
    }

    return subject.asObservable().pipe(share());
  }

  private translateApi(texts: string[], options: TranslatableTextOptions): Observable<string[]> {
    return this.httpClient
      .post<DataResult<TranslationApiResponse[]>>(`${this.configService.config.apiHost}/apis/translation/translations`, {
        texts,
        targetLanguage: localeMapping[this.localeId],
        sourceLanguage: null,
        options,
        context: options?.context || null,
      })
      .pipe(
        mapDataResult$<TranslationApiResponse[]>(),
        map((translations: TranslationApiResponse[]) => {
          return translations.map((translation) => translation?.translatedText || null);
        }),
      );
  }

  /**
   * Generate a unique cache key for a translation request based on original text and options.
   */
  private getCacheKey(originalText: string, options: TranslatableTextOptions): string {
    return JSON.stringify({
      originalText,
      options,
    });
  }

  private groupRequestsByOptions(requests: TranslationRequest[]): BatchGroup[] {
    const groupMap = new Map<string, BatchGroup>();

    requests.forEach((request) => {
      const key = this.getOptionsKey(request.options);

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          requests: [],
          options: request.options,
        });
      }

      groupMap.get(key)!.requests.push(request);
    });

    return Array.from(groupMap.values());
  }

  private getOptionsKey(options: TranslatableTextOptions): string {
    return JSON.stringify(options);
  }

  private processGroup(group: BatchGroup): void {
    const textsToTranslate = group.requests.map((req) => req.text.original);

    this.translateApi(textsToTranslate, group.options).subscribe({
      next: (translations: string[]) => {
        group.requests.forEach((request, index) => {
          const translatedText = translations[index] || request.text.original;

          const cacheKey = this.getCacheKey(request.text.original, request.options);
          this.translationCache.set(cacheKey, translatedText);

          request.subject.next(translatedText);
          request.subject.complete();
        });
        this.translationsLoadedSubject.next();
      },
      error: (error) => {
        console.error('Translation batch failed', error);
        // Fallback to original text on error (don't cache errors)
        group.requests.forEach((request) => {
          request.subject.next(request.text.original);
          request.subject.complete();
        });
      },
    });
  }
}
